import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  IconButton,
  Menu,
  Paragraph,
  Tag,
  Tooltip,
} from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import { LoadingButton, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import { useSelector } from '../../../../selectors/useSelector';
import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';
import SimpleCard from '../../../SimpleCard';
import ConnectedStatus from '../../../ConnectedStatus';
import WhoHasSignedDescription from '../WhoHasSignedDescription';

import ManageTenantsCSEvents from '../../../../model/ClickstreamEvent/ManageTenantsCSEvents';

import { NoUnderlineAnchor, singleTextLineStyles } from '../../../../styles';

import type { PAYMENT_STATUS_TYPE } from '../../../../constants/PaymentConstants';

type LeaseStatus = {
  text: string;
  type: (typeof PAYMENT_STATUS_TYPE)[keyof typeof PAYMENT_STATUS_TYPE];
};

type Description = {
  type: string;
  text: string;
};

type DescriptionTooltip = {
  title: string;
  text: string;
};

type CTAButton = {
  text: string;
  url: string;
  newTab?: boolean;
};

type LatestExtension = {
  status: LeaseStatus;
  dateRange?: string;
  description?: Description;
  ctaButton: CTAButton;
  overflowButtons?: Array<CTAButton>;
};

type Lease = {
  leaseId?: number;
  leaseStatus: LeaseStatus;
  leaseThumbnailUrl?: string;
  leaseName?: string;
  leaseDescription?: Description;
  descriptionTooltip?: DescriptionTooltip;
  ctaButton: CTAButton;
  overflowButtons?: Array<CTAButton>;
  signeeList?: Array<Description>;
  dateRange?: string;
  latestExtension?: LatestExtension;
};

type Props = {
  lease: Lease;
  isMultipleLeases?: boolean;
  index: number;
  displayType?: string;
  isLoading?: boolean;
};

const LeaseCard = ({ lease, index, displayType = VERTICAL_DISPLAY_TYPE.FULL, isLoading }: Props) => {
  const { user, currentProperty } = useSelector(
    (state) => ({
      user: state.user,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const {
    leaseStatus,
    leaseName,
    leaseDescription,
    ctaButton,
    overflowButtons,
    signeeList,
    descriptionTooltip,
    dateRange,
    latestExtension,
  } = lease;

  const leaseDisplay = leaseName || `Lease #${index + 1}`;

  const handlePrimaryCTAClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();

    /**
     * do not update location if the user clicks on WhoHasSignedDescription
     *
     * if the displayType is SIMPLE then the closest element to the event target will be a button
     * it's the WhoHasSignedDescription component
     */
    const closestButton = (event.target as HTMLElement).closest('button') || null;
    const path = (event.currentTarget as HTMLAnchorElement).href || null;

    if (!path || closestButton) {
      return;
    }

    if (ctaButton?.text === 'View lease') {
      ManageTenantsCSEvents.ViewGeneratedLease({
        currentProperty,
        user,
      });
    }

    window.router.updateLocation(path);
  };

  const renderMenu = (menuItems?: Array<CTAButton>) => {
    if (menuItems && menuItems.length) {
      return menuItems.map(({ text, url, newTab }, i) => (
        <Menu.Item asChild key={`anchor-${i}`}>
          <a href={url} target={newTab ? '_blank' : '_self'} rel={newTab ? 'noopener noreferrer' : undefined}>
            <Menu.ItemLabel>{text}</Menu.ItemLabel>
          </a>
        </Menu.Item>
      ));
    }

    return null;
  };

  const renderTooltip = (tooltipConfig?: DescriptionTooltip) => {
    if (!tooltipConfig) {
      return null;
    }

    return (
      <Tooltip.Root>
        <Tooltip.Trigger />
        <Tooltip.Portal>
          <Tooltip.Content>
            <Paragraph textStyle="body-bold" css={{ paddingBottom: '200' }}>
              {tooltipConfig.title}
            </Paragraph>
            <Paragraph textStyle="body-sm">{tooltipConfig.text}</Paragraph>
            <Tooltip.Arrow />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  };

  const renderDescription = (description?: Description, whoHasSignedList: Array<Description> = []) => {
    if (isLoading || (description && description.type && description.text)) {
      switch (description?.type) {
        case 'active':
          return (
            <Box css={{ marginTop: '400' }}>
              <WhoHasSignedDescription signeeList={whoHasSignedList} />
            </Box>
          );
        case 'caution':
          return (
            <Paragraph
              data-test="connected-caution-description"
              textStyle="body-sm"
              css={{ marginTop: '200', color: 'text.feedback.critical.strong' }}
            >
              {description.text}
            </Paragraph>
          );
        default:
          return (
            <LoadingParagraph
              data-test="connected-neutral-description"
              textStyle="body-sm"
              title={description?.text}
              isLoading={Boolean(isLoading)}
              loadingWidth="20%"
              css={{ marginTop: '200' }}
            >
              {description?.text} {renderTooltip(descriptionTooltip)}
            </LoadingParagraph>
          );
      }
    }

    return null;
  };

  const cardContent = useMemo(() => {
    return (
      <>
        <LoadingParagraph
          textStyle="body-sm-bold"
          title={leaseDisplay}
          isLoading={Boolean(isLoading)}
          loadingWidth="100%"
          css={{ marginBottom: '200', ...singleTextLineStyles, fontSize: { lg: 'body' } }}
        >
          {leaseDisplay}
        </LoadingParagraph>
        {latestExtension && (
          <>
            <Box css={{ display: 'flex', gap: '200', flexWrap: 'wrap' }}>
              <ConnectedStatus message={latestExtension?.status?.text} type={latestExtension?.status.type} size="sm" />
              {latestExtension && (
                <Tag tone="info" size="sm">
                  Lease renewal
                </Tag>
              )}
            </Box>
            {latestExtension?.dateRange && (
              <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                {latestExtension.dateRange}
              </Paragraph>
            )}
            {renderDescription(latestExtension?.description, signeeList)}
            {displayType === VERTICAL_DISPLAY_TYPE.FULL && (
              <>
                <ButtonGroup aria-label="lease renewal action buttons" css={{ marginTop: '400' }}>
                  <Button asChild tone="brand" emphasis="filled" fluid={true} onClick={handlePrimaryCTAClick}>
                    <a href={latestExtension?.ctaButton.url}>{latestExtension?.ctaButton.text}</a>
                  </Button>
                  {latestExtension?.overflowButtons && latestExtension?.overflowButtons.length > 0 && (
                    <Menu
                      content={renderMenu(latestExtension?.overflowButtons)}
                      trigger={
                        <IconButton title="More actions" tone="brand" emphasis="outlined" icon={<IconMoreFilled />} />
                      }
                    />
                  )}
                </ButtonGroup>
                <Divider css={{ marginBlock: '400' }} />
                <Paragraph
                  textStyle="body-sm-bold"
                  css={{
                    marginBottom: '200',
                    ...singleTextLineStyles,
                  }}
                >
                  Original lease
                </Paragraph>
              </>
            )}
          </>
        )}
        {(displayType === VERTICAL_DISPLAY_TYPE.FULL ||
          (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE && !latestExtension)) && (
          <>
            <Box css={{ display: 'flex', gap: '200', flexWrap: 'wrap' }}>
              {isLoading ? (
                <LoadingText textStyle="body-xs" loadingWidth="20%" isLoading={true}>
                  Loading...
                </LoadingText>
              ) : (
                <ConnectedStatus message={leaseStatus.text} type={leaseStatus.type} size="sm" />
              )}
            </Box>
            {dateRange && (
              <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                {dateRange}
              </Paragraph>
            )}
            {renderDescription(leaseDescription, signeeList)}
          </>
        )}
        {displayType === VERTICAL_DISPLAY_TYPE.FULL && (
          <ButtonGroup aria-label="leasing action buttons" css={{ marginTop: '400' }}>
            {isLoading ? (
              <Box css={{ display: 'flex', justifyContent: 'space-between', flex: '1' }}>
                <LoadingButton isLoading={true} loadingWidth="90%">
                  Loading
                </LoadingButton>
                <LoadingButton isLoading={true} loadingWidth="9%">
                  Loading
                </LoadingButton>
              </Box>
            ) : (
              <>
                <Button
                  asChild
                  tone="brand"
                  emphasis="filled"
                  fluid
                  onClick={handlePrimaryCTAClick}
                  data-eventid={
                    ctaButton.text === 'View lease' ? ManageTenantsCSEvents.ViewGeneratedLease.data_event_id : undefined
                  }
                >
                  <a
                    href={ctaButton.url}
                    target={ctaButton?.newTab ? '_blank' : '_self'}
                    rel={ctaButton?.newTab ? 'noopener noreferrer' : undefined}
                  >
                    {ctaButton.text}
                  </a>
                </Button>
                {overflowButtons && overflowButtons.length > 0 && (
                  <Menu
                    content={renderMenu(overflowButtons)}
                    trigger={
                      <IconButton title="More actions" tone="brand" emphasis="outlined" icon={<IconMoreFilled />} />
                    }
                  />
                )}
              </>
            )}
          </ButtonGroup>
        )}
      </>
    );
  }, [isLoading]);

  const renderCard = () => {
    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return (
        <SimpleCard isDisabled={true} className="LeaseCard" cursor="pointer" withChevron>
          {cardContent}
        </SimpleCard>
      );
    }

    return (
      <Card className="LeaseCard" tone="neutral" elevated={false}>
        {cardContent}
      </Card>
    );
  };

  const withAnchor = ({ ctaButton, latestExtension }: { ctaButton: CTAButton; latestExtension?: LatestExtension }) => {
    const buttonToCheck = latestExtension?.ctaButton || ctaButton;
    return (
      <NoUnderlineAnchor
        target="_self"
        href={latestExtension ? latestExtension?.ctaButton?.url : ctaButton?.url}
        onClick={handlePrimaryCTAClick}
        data-eventid={
          buttonToCheck?.text === 'View lease' ? ManageTenantsCSEvents.ViewGeneratedLease.data_event_id : undefined
        }
      >
        {renderCard()}
      </NoUnderlineAnchor>
    );
  };

  return (
    <>{displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? withAnchor({ ctaButton, latestExtension }) : renderCard()}</>
  );
};

export default LeaseCard;
